import PropTypes from "prop-types";
import React from "react";
import { Box, Collapse, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Checklist from "../../Checklist/Checklist";
import { Field, useFormikContext } from "formik";
import { homeworkSubmissionTypes } from "../../../constants";
import { CheckboxWithLabel } from "formik-mui";
import { Select, TextField } from "../../FormikFields";
import { useTheme } from "@mui/material/styles";
import { groupBy, filter, unionBy, reject, pull } from "lodash";
import { findByKey } from "../../../utils/helpers";

const HomeworkFileFormatField = ({ allFileFormats }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();

  let fileFormats = values.fileFormats;
  const fileFormatGroups = groupBy(allFileFormats, "category");

  const handleSelectAllChange = category => checked => {
    fileFormats = checked
      ? unionBy(fileFormatGroups[category], fileFormats, "id")
      : reject(fileFormats, { category });
    setFieldValue("fileFormats", fileFormats);
  };

  const handleChange = (checked, fileFormat) => {
    let newFormats = [...fileFormats];
    if (checked) {
      newFormats = [...newFormats, fileFormat];
    } else {
      pull(newFormats, fileFormat);
    }
    setFieldValue("fileFormats", newFormats);
  };

  return (
    <Box
      bgcolor={theme.palette.secondary[50]}
      pl={5}
      py={4.5}
      pr={{ xs: 5, lg: 11.5 }}
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
    >
      {fileFormatGroups &&
        Object.keys(fileFormatGroups).map(category => (
          <Box mb={{ xs: 4, sm: 0 }} key={category}>
            <Checklist
              key={category}
              selectAllCheckbox={{
                label: t(`editHomework.fileFormatSelectAllLabels.${category}`),
                value: category,
                checked:
                  filter(fileFormats, { category }).length ===
                  fileFormatGroups[category].length
              }}
              checkboxes={fileFormatGroups[category].map(fileFormat => ({
                label: fileFormat.extension,
                value: fileFormat,
                checked: !!findByKey(fileFormats, "id", fileFormat.id)
              }))}
              onChange={handleChange}
              onSelectAllChange={handleSelectAllChange(category)}
            />
          </Box>
        ))}
    </Box>
  );
};

const EditHomeworkSettings = ({ allFileFormats, userEmail }) => {
  const { t } = useTranslation();
  const { values, errors, touched } = useFormikContext();

  return (
    <Box display="flex" flexDirection="column" py={1} height="100%">
      <Grid container spacing={3}>
        <Grid item xs={12} sm={8}>
          <TextField
            label={t("editHomework.titleFieldLabel")}
            name={"title"}
            inputProps={{
              "data-qa": "homework-modal-title-field"
            }}
            helperText={touched.title && errors.title}
            error={touched.title && !!errors.title}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Select
            name="submissionType"
            disableUnderline
            menuItems={[
              {
                label: t("editHomework.submissionTypeFile"),
                value: homeworkSubmissionTypes.file
              },
              {
                label: t("editHomework.submissionTypeFileAndText"),
                value: homeworkSubmissionTypes.fileAndText
              }
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <Field
            component={CheckboxWithLabel}
            name="restrictFileFormat"
            inputProps={{
              "data-qa": "restrict-file-format-checkbox"
            }}
            Label={{
              label: (
                <Typography variant="subtitle1">
                  {t("editHomework.restrictFileFormatLabel")}
                </Typography>
              )
            }}
            color="primary"
            type="checkbox"
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <Collapse
            in={values.restrictFileFormat}
            data-qa="restrict-file-format-collapse"
          >
            {!!allFileFormats && (
              <HomeworkFileFormatField
                allFileFormats={allFileFormats}
                selectedFormats={values.fileFormats}
              />
            )}
          </Collapse>
        </Grid>
      </Grid>
    </Box>
  );
};

EditHomeworkSettings.propTypes = {
  allFileFormats: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      mime: PropTypes.string,
      category: PropTypes.string,
      extension: PropTypes.string
    })
  ),
  userEmail: PropTypes.string
};

export default EditHomeworkSettings;
